import { get } from '../../utils/object-helpers';
import { ajax } from '../../utils/ajax-handler';
import storeEvent from '../../utils/store-event';

export default class {
    // Properties on the input:
    // page, itemId, iconSize, itemCardWatch, quickviewWatch
    init(input) {
        let watch, watching, isWatching;
        if (!input.itemCardWatch && !input.quickviewWatch) {
            return;
        }
        if (input.quickviewWatch) {
            watch = get(input, 'quickviewWatch.offCallToAction');
            watching = get(input, 'quickviewWatch.onCallToAction');
            isWatching = get(input, 'quickviewWatch.isWatching');
        } else {
            watch = get(input, 'itemCardWatch.watch');
            watching = get(input, 'itemCardWatch.watching');
            isWatching = get(input, 'itemCardWatch.isWatching');
        }
        const baseUrl = get(input, 'page.info.baseUrl');
        const unwatchToken = get(input, 'page.info.tokens.unwatchToken');
        const watchToken = get(input, 'page.info.tokens.watchToken');

        this.itemId = input.itemId;
        this.iconSize = input.iconSize;
        this.addWatchAjax = this.getAddWatchAjax(this.itemId, baseUrl, watch, watchToken);
        this.removeWatchAjax = this.getRemoveWatchAjax(this.itemId, baseUrl, watching, unwatchToken);
        // Watch won't work if no token, so not rendering it lets tests catch it.
        this.hasToken = unwatchToken || watchToken;
        this.isLoggedIn = get(input, 'page.info.isLoggedIn');
        this.hasVariations = input.hasVariations;
        this.state = {
            error: null,
            isWatching,
            variationId: input.variationId,
            isVariationWatched: input.isVariationWatched
        };
    }
    onCreate(input) {
        this.init(input);
    }
    onInput(input) {
        this.init(input);
        // onInput is called everytime quickview state is changed (hero image changes, aspect changes, etc.)
        // Only reset this.watchMap when quickview is first opened.
        if (!this.watchMap) {
            this.watchMap = [];
        }
        if (this.hasVariations && this.state.variationId && this.state.variationId !== -1) {
            // The watchMap takes precedence because it's only changed by the toggle() action
            // which updates state via ajax. The info from quickview is static and doesn't change
            // after quickview has been opened (it changes between openings though).
            const valueInWatchMap = this.watchMap[this.state.variationId];
            if (valueInWatchMap !== undefined) {
                this.state.isWatching = valueInWatchMap;
            } else {
                this.state.isWatching = this.state.isVariationWatched;
            }
        } else if (this.hasVariations) { // If menu has been unselected, there's no variationId
            this.state.isWatching = false;
        }
        return {
            iconSize: input.iconSize,
            addWatchAjax: this.addWatchAjax,
            removeWatchAjax: this.removeWatchAjax,
            hasToken: this.hasToken
        };
    }
    onMount() {
        // There can be more than one Watch icon for the same item.
        // Need to update our watch status if one of them is updated so
        // that all the icons reflect the correct watch status for that item.
        // But for now, don't do this for items with variations.
        if (this.el && !this.hasVariations) {
            storeEvent.watchSuccess.listen(result => {
                if (result.itemId === this.itemId) {
                    this.state.isWatching = result.isWatching;
                }
            });
        }
    }
    toggleWatch(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        // In order to watch an item with variations, a variationId is needed.
        // If the user is logged in and item doesn't have a variationId,
        // dispatch event to the quickview, then exit function.
        //
        // The quickview will display message asking user to make the required selections
        // to get a variationId. When quickview gets a variationId, our onInput() will be
        // triggered and the variationId will in the "input" param.
        if (this.isLoggedIn && this.hasVariations && (!this.state.variationId || this.state.variationId === -1)) {
            storeEvent.watchAttempt.dispatch(this.itemId);
            return;
        }
        this.toggleSpinner(true);
        const watchAjax = this.state.isWatching ? this.removeWatchAjax : this.addWatchAjax;
        if (watchAjax && this.hasVariations && this.state.variationId !== -1) {
            watchAjax.url = `${watchAjax.url }&variationId=${this.state.variationId}`;
        }
        const trackingData = get(watchAjax, 'tracking', null);
        const trackingObj = trackingData ? JSON.parse(trackingData) : null;
        if (trackingObj) {
            document.dispatchEvent(new CustomEvent('pulsar', { detail: trackingObj }));
        }
        ajax({
            url: watchAjax.url,
            responseType: 'json',
            withCredentials: false
        }, {
            successCallBack: this.handleWatchSuccess.bind(this),
            errorCallBack: this.handleWatchFailure.bind(this)
        });
    }
    handleWatchSuccess(response) {
        this.toggleSpinner(false);
        const redirectResponse = get(response, 'modules.redirect.reload', null);
        const watchResponse = get(response, 'listDetails.[0].itemAdded', null);
        const unwatchResponse = get(response, 'responses.[0].statusCode', '');
        if (redirectResponse === true) {
            window.location.href = get(response, 'modules.redirectUrl', '');
        } else if (watchResponse === true) {
            this.state.isWatching = true;
        } else if (unwatchResponse === '200') {
            this.state.isWatching = false;
        }

        // Keep a record of the variations that were toggled.
        if (this.hasVariations && this.state.variationId && this.state.variationId !== -1) {
            this.watchMap[this.state.variationId] = this.state.isWatching;
        }

        storeEvent.watchSuccess.dispatch({
            itemId: this.itemId,
            isWatching: this.state.isWatching
        });
    }
    handleWatchFailure() {
        this.toggleSpinner(false);
    }
    getAddWatchAjax(itemId, baseUrl, watch, token) {
        return {
            url: `${baseUrl}/mye/myebay/ajax/watch/watchInline?itemId=${itemId}&srt=${token}`,
            tracking: this.getTracking(watch),
            title: this.getTitle(watch)
        };
    }
    getRemoveWatchAjax(itemId, baseUrl, watching, token) {
        return {
            url: `${baseUrl}/mye/myebay/ajax/watch/unwatchInline?itemId=${itemId}&srt=${token}`,
            tracking: this.getTracking(watching),
            title: this.getTitle(watching)
        };
    }
    getUrl(watchObject, token) {
        const url = get(watchObject, 'action.URL', '');
        return token ? `${url}&srt=${token}` : url;
    }
    getTracking(watchObject) {
        return JSON.stringify(get(watchObject, 'action.trackingList.0', {}));
    }
    getTitle(watchObject) {
        return get(watchObject, 'text.accessibilityText', '') || get(watchObject, 'text', '');
    }
    toggleSpinner(isShowSpinner) {
        const watchStyle = isShowSpinner ? 'none' : 'block';
        const spinnerStyle = isShowSpinner ? 'block' : 'none';
        this.getEl('watchBtn').style.display = watchStyle;
        this.getEl('watchBtn').nextSibling.style.display = spinnerStyle;
    }
}
